import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import Link from react-router-dom
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  ButtonGroup,
  Tooltip,
  Chip
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { getJobOrders } from "../API/jobOrderAPI";
import { getCacheClients } from "../API/clientAPI";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { styled } from "@mui/material/styles";
import BaseWrapper from "../../components/shared/BaseWrapper";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchAndFilterJobOrders } from "../JobOrders/utils/JobOrderFilter";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

const getTodayInPH = () => {
  const now = new Date();
  const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  );
  localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  return localDate.toISOString().split("T")[0];
};

function ManageServiceReports() {
  const [jobOrders, setJobOrders] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [clientsCache, setClientsCache] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [totalJobOrders, setTotalJobOrders] = useState(null);
  const [isDateRange, setIsDateRange] = useState(false);
  // const [searchFilter, setSearchFilter] = useState({
  //   client: "",
  //   startDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth(),
  //       1, // First day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth() + 1,
  //       0, // Last day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endorsementForm: "",
  // });

  const [searchFilter, setSearchFilter] = useState({
    client: "",
    startDate: getTodayInPH(),
    endDate: getTodayInPH(),
    endorsementForm: "",
  });

  const handleSingleDateChange = (value) => {
    setSearchFilter((prev) => ({
      ...prev,
      startDate: value,
      endDate: value,
    }));
  };

  const handleDateRangeToggle = () => {
    setIsDateRange((prev) => {
      const next = !prev;

      if (!next) {
        setSearchFilter((current) => ({
          ...current,
          endDate: current.startDate,
        }));
      }

      return next;
    });
  };

  // get local user data to be used for tech view filter
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles     = parsedUser.roles      || [];
  const technicianId = parsedUser.technician_id || "";
  // –––

  const navigate = useNavigate();

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        setClientsLoading(true);
        // Fetch clients
        const clientsCache = await getCacheClients(); // Pass status if needed

        setClientsCache(clientsCache);
      } catch (err) {
        setError(err.message);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchCacheClients();
  }, []);
  
  const handleSearchButtonClick = async () => {
    try {
      setIsLoading(true);
      const { list, total } = await fetchAndFilterJobOrders({
        searchFilter,                // includes client + dates + endorsementForm
        status: [],                  // no status filter
        serviceReportStatus: ["Done"],
        billingRecordStatus: [],
        userRoles,
        technicianId,
        getJobOrdersApi: getJobOrders
      });
      setJobOrders(list);
      setTotalJobOrders(total);
    } catch (err) {
      setError(err.message || String(err));
      setErrorMessage("Failed to fetch Service Reports. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchClient = (event, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      client: value,
    }));
  };

  // map your form_type → URL segment
  const formTypeToSegment = {
    makati_gpc_form:       "makati-gpc-form",
    qc_form:               "qc-form",
    termite_control_form:  "termite-control-form",
    transmittal_form:      "transmittal-form",
  };

  // build the right route for view / edit / delete
  const getReportRoute = (job, action) => {
    const segment = formTypeToSegment[job.form_type] || "service-report";
    const currentRoute = "/manage-service-reports"; // Current page route
    return `/${action}-${segment}/${job.id}?returnTo=${encodeURIComponent(currentRoute)}`;
  };


  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Manage Service Reports"
      subTitleRoute={"/manage-service-reports"}
      createButtonLabel="Create Service Report"
      onClickCreateButton={() => navigate("/create-service-report")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          alignItems="stretch"
          sx={{
            mb: 2,
            "& .MuiFormControl-root": {
              height: "100%",
            },
            "& .MuiOutlinedInput-root": {
              minHeight: 40,
            },
            "& .MuiButton-root.filter-action-btn": {
              minHeight: 40,
            },
          }}
        >
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={clientsCache}
              getOptionLabel={(client) => client?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, params) => clientFilter(options, params)}
              openOnFocus
              autoHighlight
              loading={clientsLoading}
              noOptionsText={clientsLoading ? "Loading..." : "No clients found"}
              value={searchFilter.client || null}
              onChange={(event, newValue) => handleSearchClient(event, newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{option.name}</span>
                    <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Name"
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error ? error : null}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
                height: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {!isDateRange ? (
                <TextField
                  label="Job Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={searchFilter.startDate}
                  onChange={(e) => handleSingleDateChange(e.target.value)}
                />
              ) : (
                <>
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={searchFilter.startDate}
                    onChange={(e) =>
                      setSearchFilter((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                  />

                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={searchFilter.endDate}
                    inputProps={{
                      min: searchFilter.startDate || undefined,
                    }}
                    onChange={(e) =>
                      setSearchFilter((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                  />
                </>
              )}

              <Button
                className="filter-action-btn"
                variant={isDateRange ? "contained" : "outlined"}
                onClick={handleDateRangeToggle}
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: { xs: "100%", sm: 140 },
                  flexShrink: 0,
                }}
              >
                {isDateRange ? "Single Date" : "Date Range"}
              </Button>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={2}
            sx={{
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Button
              className="filter-action-btn"
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick}
              disabled={isLoading}
              fullWidth
              sx={{
                height: "100%",
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />
      {jobOrders.length > 0 && (
        <>
          <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
            <Grid item xs={2}>
              <Card
                sx={{
                  backgroundColor: '#1976D2',
                  color: '#FFF',
                  borderRadius: 1,
                  textAlign: 'center',
                  padding: '4px',  // Minimal padding for a compact look
                  boxShadow: 1
                }}
              >
                <CardContent sx={{ padding: '4px' }}>  {/* Reduced internal padding */}
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Total Job Orders
                  </Typography>
                  <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                    {totalJobOrders}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ padding: "20px" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>Job Date</b>
                  </TableCell>
                  <TableCell>
                    <b>Start Time</b>
                  </TableCell>
                  <TableCell>
                    <b>End Time</b>
                  </TableCell>
                  <TableCell>
                    <b>Client</b>
                  </TableCell>
                  <TableCell>
                    <b>Job Type</b>
                  </TableCell>
                  <TableCell>
                    <b>Technicians</b>
                  </TableCell>
                  <TableCell>
                    <b>Action</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobOrders.map((job, index) => (
                  <TableRow key={index}>
                    <TableCell>{job.job_date || ""}</TableCell>
                    <TableCell>{job.start_time || ""}</TableCell>
                    <TableCell>{job.end_time || ""}</TableCell>
                    <TableCell>{job.client || ""}</TableCell>
                    <TableCell>
                      {job.job_types.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {job.job_types.map((type, i) => (
                            <Chip
                              key={i}
                              label={type.replace(/_/g, " ")}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : (
                        ""
                      )}
                    </TableCell>
                    <TableCell>
                      {job.technicians.length > 0
                        ? job.technicians.map((tech, i) => (
                          <div
                            key={i}
                          >{`${tech.firstname} ${tech.lastname}`}</div>
                        ))
                        : ""}
                    </TableCell>
                    <TableCell>                      
                      <ButtonGroup
                        variant="outlined"
                        size="small"
                        aria-label="action button group"
                        sx={{ gap: 0, padding: 0 }}
                      >
                        <Tooltip title="View Service Report" arrow>
                          <Button
                            onClick={() => navigate(getReportRoute(job, "view"))}
                            startIcon={<VisibilityIcon />}
                            sx={{
                              textTransform: "none",
                              minWidth: "32px",
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>

                        <Tooltip title="Edit Service Report" arrow>
                          <Button
                            onClick={() => {
                              const route = getReportRoute(job, "edit");
                              if (route) {
                                navigate(route);
                              }
                            }}
                            startIcon={<EditIcon />}
                            sx={{
                              textTransform: "none",
                              minWidth: "32px",
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>

                        <Tooltip title="Delete Service Report" arrow>
                          <Button
                            onClick={() => navigate(getReportRoute(job, "delete"))}
                            startIcon={<DeleteIcon />}
                            color="error"
                            sx={{
                              textTransform: "none",
                              minWidth: "32px",
                              padding: "6px",
                              borderRadius: "4px",
                            }}
                          />
                        </Tooltip>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </BaseWrapper>
  );
}export default ManageServiceReports;
